import { type ComponentProps, useCallback, useState } from 'react'
import { toast } from 'sonner'

import { useTodoStore } from '@/store/useTodoStore'

import { applySchedule } from './schedule-actions'
import type { SlotCreateDialog } from './SlotCreateDialog'

type DialogProps = ComponentProps<typeof SlotCreateDialog>
type SlotOptions = Pick<DialogProps, 'defaultMode' | 'disabledModes'>
type Slot = SlotOptions & { start: Date; end: Date }

/** "HH:MM" を base と同じ日の日時にする。終了が開始以前なら翌日扱い */
const applyTimes = (base: Date, startTime: string, endTime: string) => {
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  const start = new Date(base)
  start.setHours(sh, sm, 0, 0)
  const end = new Date(base)
  end.setHours(eh, em, 0, 0)
  if (end <= start) end.setDate(end.getDate() + 1)
  return { start, end }
}

/** 空きスロットから TODO / 予定を作る・既存 TODO を配置するダイアログの状態と処理 */
export const useSlotCreate = () => {
  const todos = useTodoStore((s) => s.todos)
  const addTodo = useTodoStore((s) => s.addTodo)
  const [slot, setSlot] = useState<Slot | null>(null)

  const openSlot = useCallback((start: Date, end: Date, options: SlotOptions = {}) => {
    setSlot({ start, end, ...options })
  }, [])

  if (!slot) return { openSlot, dialogProps: null }

  const { start, end, defaultMode, disabledModes } = slot
  const createScheduled = async (title: string, extra: { project?: string | null; is_event?: boolean }) => {
    await addTodo({
      title,
      scheduled_start: start.toISOString(),
      scheduled_end: end.toISOString(),
      status: 'scheduled',
      ...extra,
    })
  }

  const dialogProps: DialogProps = {
    range: { start: start.toISOString(), end: end.toISOString() },
    defaultMode,
    disabledModes,
    todos,
    onClose: () => setSlot(null),
    onTimeChange: (s, e) => setSlot((prev) => (prev ? { ...prev, ...applyTimes(prev.start, s, e) } : prev)),
    onCreateTask: async (title, project) => {
      await createScheduled(title, { project })
      toast.success('Todo を作成しました')
    },
    onCreateEvent: async (title) => {
      await createScheduled(title, { is_event: true })
      toast.success('予定を作成しました')
    },
    onScheduleExisting: async (todoId) => {
      await applySchedule(todoId, start, end)
      toast.success('スケジュールを設定しました')
    },
  }
  return { openSlot, dialogProps }
}
